import { useEffect, useMemo, useState, type FC } from "react";
import type { IconType } from "react-icons";
import { MdFolder, MdCloudQueue, MdOutlineCreateNewFolder, MdSync, MdArrowUpward, MdArrowDownward } from "react-icons/md";
import { getClientConfig, getUserFavorites, removeUserFavorite, setFolderHiddenStatus, syncLegacyFolder, updateFolderBodyStoragePreference, updateFolderKeywords } from "../../apiClient";
import FavoriteRenameModal from "./FavoriteRenameModal";
import ContextMenu, { type ContextMenuItem } from "./ContextMenu";
import { useAppContext } from "../contexts/AppContext";
import { getSetting, setSetting } from "../taskpane";
import "../styles/FolderList.css";
import type { FolderItem, FavoriteType } from "../../../../common/src/types/types";

interface ContextMenuState {
    visible: boolean;
    x: number;
    y: number;
    folderId: string | null;
}

interface KeywordEditModal {
    visible: boolean;
    folderId: string | null;
    currentKeywords: string[];
}

interface FavoriteRenameModalState {
    visible: boolean;
    folderId: string | null;
    currentName: string;
}

const folderTypeMeta: Record<FavoriteType, { Icon: IconType; tooltip: string; className: string }> = {
    project: {
        Icon: MdFolder,
        tooltip: "Project folder",
        className: "project",
    },
    onedrive: {
        Icon: MdCloudQueue,
        tooltip: "Private OneDrive folder",
        className: "onedrive",
    },
    custom: {
        Icon: MdOutlineCreateNewFolder,
        tooltip: "Custom folder",
        className: "custom",
    },
};

const FolderList: FC = () => {
    const {
        searchTerm,
        selectedFolder,
        setSelectedFolder,
        navigateToProject,
        navigateToAddFavorites,
        navigateToSave,
        setFavorites,
        quickSaveEmails,
    } = useAppContext();

    const [sortMode, setSortMode] = useState(() => getSetting('folderSortMode', 'alphabetical'));
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(() => getSetting('folderSortOrder', 'asc'));

    const [folders, setFolders] = useState<FolderItem[]>([]);
    const [showHidden, setShowHidden] = useState(() => getSetting('folderShowHidden', false));
    const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({
        visible: false,
        x: 0,
        y: 0,
        folderId: null,
    });

    const [keywordEditModal, setKeywordEditModal] = useState<KeywordEditModal>({
        visible: false,
        folderId: null,
        currentKeywords: [],
    });
    const [favoriteRenameModal, setFavoriteRenameModal] = useState<FavoriteRenameModalState>({
        visible: false,
        folderId: null,
        currentName: "",
    });

    const [editingKeywords, setEditingKeywords] = useState<string[]>([]);
    const [newKeywordInput, setNewKeywordInput] = useState<string>("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [projectRootPath, setProjectRootPath] = useState("");

    useEffect(() => {
        getClientConfig()
            .then((config) => setProjectRootPath(config.projectRootPath))
            .catch((err) => console.error("Failed to load client config", err));
    }, []);

    useEffect(() => {
        let isActive = true;
        const loadFavorites = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const favorites = await getUserFavorites();
                if (!isActive) {
                    return;
                }
                if (favorites) {
                    setFolders(favorites);
                    setFavorites(favorites);
                } else {
                    setFolders([]);
                }
            } catch (err) {
                console.error("Failed to load favorites", err);
                if (isActive) {
                    setError("Unable to load favorites. Please try again.");
                    setFolders([]);
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };
        loadFavorites();
        return () => {
            isActive = false;
        };
    }, [setFavorites]);

    const filteredFolders = useMemo(() => {
        return folders.filter((folder) => {
            const matchesSearch = folder.name.toLowerCase().includes(searchTerm.toLowerCase());
            const isHidden = folder.hidden === true;
            return matchesSearch && (showHidden || !isHidden);
        });
    }, [folders, searchTerm, showHidden]);

    const sortedFolders = useMemo(() => {
        return [...filteredFolders].sort((a, b) => {
            switch (sortMode) {
                case "alphabetical":
                    const comparison = a.name.localeCompare(b.name);
                    return sortOrder === "asc" ? comparison : -comparison;
                case "recent":
                    const diff = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
                    return sortOrder === "asc" ? diff : -diff;
                case "type":
                    const typeComparison = a.favoriteType.localeCompare(b.favoriteType);
                    return sortOrder === "asc" ? typeComparison : -typeComparison;
                default:
                    return 0;
            }
        });
    }, [filteredFolders, sortMode, sortOrder]);

    const toggleHiddenVisibility = () => {
        const newValue = !showHidden;
        setShowHidden(newValue);
        setSetting('folderShowHidden', newValue);
    };

    const handleRightClick = (e: React.MouseEvent, folderId: string) => {
        e.preventDefault();
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            folderId,
        });
    };

    const handleBlankAreaDrop = (_event: React.DragEvent<HTMLDivElement>) => {
        _event.preventDefault();
        navigateToSave();
    };

    const hideFolder = async (folderId: string) => {
        closeContextMenu();
        await setFolderHiddenStatus(folderId, true).then(() => {
            setFolders(prev => prev.map(folder =>
                folder.id === folderId
                    ? { ...folder, hidden: true }
                    : folder
            ));
            setFavorites(prev => prev.map(folder =>
                folder.id === folderId
                    ? { ...folder, hidden: true }
                    : folder
            ));
        });

    };

    const unhideFolder = async (folderId: string) => {
        closeContextMenu();
        await setFolderHiddenStatus(folderId, false).then(() => {
            setFolders(prev => prev.map(folder =>
                folder.id === folderId
                    ? { ...folder, hidden: false }
                    : folder
            ));
            setFavorites(prev => prev.map(folder =>
                folder.id === folderId
                    ? { ...folder, hidden: false }
                    : folder
            ));
        });
    };

    const closeContextMenu = () => {
        setContextMenu({ visible: false, x: 0, y: 0, folderId: null });
    };

    const handleRemoveFavorite = async (folderId: string) => {
        closeContextMenu();
        try {
            setFolders(prev => prev.filter(folder => folder.id !== folderId));
            setFavorites(prev => prev.filter(folder => folder.id !== folderId));
            await removeUserFavorite(folderId);
        } catch (error) {
            console.error("Failed to remove favorite folder:", error);
        }
    };

    const handleRenameFavorite = (folderId: string) => {
        closeContextMenu();
        const folder = folders.find(f => f.id === folderId);
        if (folder) {
            setFavoriteRenameModal({
                visible: true,
                folderId,
                currentName: folder.name,
            });
        }
    };


    const handleEditKeywords = (folderId: string) => {
        const folder = folders.find(f => f.id === folderId);
        const currentKeywords = folder?.keywords || [];
        setKeywordEditModal({
            visible: true,
            folderId,
            currentKeywords,
        });
        setEditingKeywords([...currentKeywords]);
        closeContextMenu();
    };

    const handleSaveKeywords = async () => {
        if (!keywordEditModal.folderId) return;

        try {
            await updateFolderKeywords(keywordEditModal.folderId, editingKeywords);

            // Update local state
            setFolders(prev => prev.map(folder =>
                folder.id === keywordEditModal.folderId
                    ? { ...folder, keywords: editingKeywords }
                    : folder
            ));

            // Update context favorites
            setFavorites(prev => prev.map(folder =>
                folder.id === keywordEditModal.folderId
                    ? { ...folder, keywords: editingKeywords }
                    : folder
            ));

            // Close modal
            setKeywordEditModal({ visible: false, folderId: null, currentKeywords: [] });
            setEditingKeywords([]);
            setNewKeywordInput("");
        } catch (error) {
            console.error('Failed to save keywords:', error);
        }
    };

    const handleSort = (criteria: string) => {
        const nextOrder = criteria === sortMode && sortOrder === 'asc' ? 'desc' : 'asc';
        if (criteria !== sortMode) {
            setSortMode(criteria);
            setSetting('folderSortMode', criteria);
        }
        setSortOrder(nextOrder);
        setSetting('folderSortOrder', nextOrder);
    };

    const onToggleBodyStorage = async (folderId: string, enabled: boolean) => {
        try {
            await updateFolderBodyStoragePreference(folderId, enabled);
            setFolders(prev => prev.map(folder =>
                folder.id === folderId
                    ? { ...folder, doNotStoreBody: enabled }
                    : folder
            ));
            setFavorites(prev => prev.map(folder =>
                folder.id === folderId
                    ? { ...folder, doNotStoreBody: enabled }
                    : folder
            ));
        } catch (error) {
            console.error('Failed to update body storage preference:', error);
        }
    };

    const handleSyncLegacy = async (folderId: string) => {
        try {
            await syncLegacyFolder(folderId);
            setFolders(prev => prev.map(folder =>
                folder.id === folderId
                    ? { ...folder, importLegacy: true }
                    : folder
            ));
            setFavorites(prev => prev.map(folder =>
                folder.id === folderId
                    ? { ...folder, importLegacy: true }
                    : folder
            ));
        } catch (error) {
            console.error('Failed to queue legacy folder sync:', error);
        }
    }

    const handleCopyPath = async (folderId: string) => {
        closeContextMenu();
        try {
            const folder = folders.find(f => f.id === folderId);
            if (folder?.folderPath) {
                const folderPath = folder.favoriteType === "project" ? projectRootPath + folder.folderPath : folder.folderPath;
                await navigator.clipboard.writeText(folderPath);
            }
            else {
                console.warn("Folder path not available for folder:", folderId);
                return;
            }
        } catch (error) {
            console.error("Failed to copy folder path:", error);
        }
    };

    const handleCancelKeywordEdit = () => {
        setKeywordEditModal({ visible: false, folderId: null, currentKeywords: [] });
        setEditingKeywords([]);
        setNewKeywordInput("");
    };

    const handleAddKeyword = () => {
        const keyword = newKeywordInput.trim();
        if (keyword && !editingKeywords.includes(keyword)) {
            setEditingKeywords(prev => [...prev, keyword]);
            setNewKeywordInput("");
        }
    };

    const handleRemoveKeyword = (keywordToRemove: string) => {
        setEditingKeywords(prev => prev.filter(k => k !== keywordToRemove));
    };

    const handleKeywordInputKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddKeyword();
        }
    };

    const contextTargetFolder = contextMenu.folderId ? folders.find((folder) => folder.id === contextMenu.folderId) : null;
    const contextAllowsBodyToggle = contextTargetFolder ? contextTargetFolder.favoriteType !== "project" : false;
    const contextDoNotStoreBody = contextTargetFolder?.doNotStoreBody === true;
    const contextImportLegacy = contextTargetFolder?.importLegacy === true;
    const contextIsHidden = contextTargetFolder?.hidden === true;

    const contextMenuItems: ContextMenuItem[] = contextMenu.folderId
        ? [
            {
                key: "edit-keywords",
                label: "Edit Keywords",
                title: "Set keywords used for suggesting this folder for archived emails",
                onClick: () => handleEditKeywords(contextMenu.folderId!),
            },
            {
                key: "rename",
                label: "Rename Favorite",
                title: "Set a custom name for this favorite, only visible to you",
                onClick: () => handleRenameFavorite(contextMenu.folderId!),
            },
            {
                key: "sync-legacy",
                label: contextImportLegacy ? "Legacy Emails Queued for Import" : "Sync Legacy Emails",
                title: contextImportLegacy
                    ? "This folder is already queued for legacy email import"
                    : "Add any previously archived emails from this folder to Project E-File if they do not exist in the database yet",
                onClick: () => { handleSyncLegacy(contextMenu.folderId!); closeContextMenu(); },
            },
            ...(contextAllowsBodyToggle
                ? [{
                    key: "toggle-body-storage",
                    label: contextDoNotStoreBody ? "Enable storing email bodies" : "Disable storing email bodies",
                    title: contextDoNotStoreBody
                        ? "Email bodies are currently not being stored, click to re-enable storage"
                        : "Click to disable storing email bodies to the E-File database, they will still be present in the archived email file. Increases privacy, but reduces search functionality",
                    onClick: () => { onToggleBodyStorage(contextMenu.folderId!, !contextDoNotStoreBody); closeContextMenu(); },
                } as ContextMenuItem]
                : []),
            contextIsHidden
                ? {
                    key: "unhide",
                    label: "Unhide Folder",
                    title: "Unhide this folder and make it visible in the favorites list",
                    onClick: () => unhideFolder(contextMenu.folderId!),
                }
                : {
                    key: "hide",
                    label: "Hide Folder",
                    title: "Hide this folder from the favorites list without removing it entirely",
                    onClick: () => hideFolder(contextMenu.folderId!),
                },
            {
                key: "remove",
                label: "Remove from Favorites",
                title: "Removes this folder from your favorites list, it will no longer appear here but any archived emails will still be in the database and can be re-added to favorites later",
                onClick: () => handleRemoveFavorite(contextMenu.folderId!),
            },
            {
                key: "copy-path",
                label: "Copy File Path",
                title: "Copies the file path of this folder to your clipboard",
                onClick: () => handleCopyPath(contextMenu.folderId!),
            },
            {
                key: "sort",
                label: "Sort Favorites",
                title: "Sort your favorite folders",
                children: [
                    { key: "alphabetical", label: <span className="sort-option-label">Alphabetical{sortMode === "alphabetical" && (sortOrder === "asc" ? <MdArrowUpward aria-label="Ascending" /> : <MdArrowDownward aria-label="Descending" />)}</span>, onClick: () => handleSort("alphabetical") },
                    { key: "recent", label: <span className="sort-option-label">Most Recent Activity{sortMode === "recent" && (sortOrder === "asc" ? <MdArrowUpward aria-label="Ascending" /> : <MdArrowDownward aria-label="Descending" />)}</span>, onClick: () => handleSort("recent") },
                ],
            }
        ]
        : [];

    return (
        <div className="folder-list">
            <div className="folder-list-scroll"
                onDragOver={(event) => {
                    event.preventDefault();
                }}
                onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragOverFolder(null);
                    handleBlankAreaDrop(event);
                }}
            >
                {isLoading && <div className="folder-list-empty">Loading favorites...</div>}
                {error && !isLoading && <div className="folder-list-empty">{error}</div>}
                {!isLoading && !error && sortedFolders.length === 0 && (
                    <div className="folder-list-empty">No favorites yet. Add one to get started.</div>
                )}
                {!isLoading && !error && sortedFolders.map((folder) => {
                    const typeInfo = folderTypeMeta[folder.favoriteType];
                    const { Icon, tooltip, className } = typeInfo;

                    return (
                        <div
                            key={folder.id}
                            className={`folder-item ${selectedFolder === folder.id ? "selected" : ""} ${folder.hidden === true ? "hidden-folder" : ""} ${dragOverFolder === folder.id ? "drag-over" : ""}`.trim()}
                            onClick={() => {
                                setSelectedFolder(folder.id);
                                navigateToProject(folder.id!);
                            }}
                            onDragEnter={(event) => {
                                event.preventDefault();
                                setDragOverFolder(folder.id);
                            }}
                            onDragOver={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                            }}
                            onDragLeave={(event) => {
                                event.preventDefault();
                                // Only reset if we're actually leaving this element, not entering a child
                                if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                                    setDragOverFolder(null);
                                }
                            }}
                            onDrop={async (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setDragOverFolder(null);
                                navigateToProject(folder.id!);
                                const saved = await quickSaveEmails(folder.id!);
                                if (!saved) {
                                    navigateToSave();
                                }
                            }}
                            onContextMenu={(e) => handleRightClick(e, folder.id!)}
                            title={folder.favoriteType === "onedrive" ? "" : folder.folderPath}
                        >
                            <span className={`folder-type-icon ${className}`} title={tooltip} aria-label={tooltip}>
                                <Icon />
                                {folder.importLegacy === true && (
                                    <span className="folder-legacy-sync-badge" title="Syncing Legacy Emails" aria-label="Syncing Legacy Emails">
                                        <MdSync />
                                    </span>
                                )}
                            </span>
                            <span className="folder-name">{folder.name}</span>
                        </div>
                    );
                })}
            </div>
            <div className="add-to-favorites-option" onClick={navigateToAddFavorites} title="Add a new archive folder to your favorites list">
                <span className="favorites-icon">★</span>
                <span className="favorites-text">Add a Folder to Favorites</span>
            </div>

            <ContextMenu
                visible={contextMenu.visible}
                x={contextMenu.x}
                y={contextMenu.y}
                items={contextMenuItems}
                onClose={closeContextMenu}
            />

            {keywordEditModal.visible && (
                <div className="keyword-edit-modal-overlay" onClick={handleCancelKeywordEdit}>
                    <div className="keyword-edit-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="keyword-edit-header">
                            <h3>Edit Keywords</h3>
                            <button className="keyword-edit-close" onClick={handleCancelKeywordEdit}>
                                ×
                            </button>
                        </div>
                        <div className="keyword-edit-body">
                            <label>Keywords:</label>
                            <div className="keyword-list">
                                {editingKeywords.map((keyword, index) => (
                                    <div key={index} className="keyword-item">
                                        <span className="keyword-text">{keyword}</span>
                                        <button
                                            className="keyword-remove-btn"
                                            onClick={() => handleRemoveKeyword(keyword)}
                                            title="Remove keyword"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                {editingKeywords.length === 0 && (
                                    <div className="keyword-empty">No keywords added yet</div>
                                )}
                            </div>
                            <div className="keyword-add-section">
                                <input
                                    type="text"
                                    className="keyword-add-input"
                                    value={newKeywordInput}
                                    onChange={(e) => setNewKeywordInput(e.target.value)}
                                    onKeyPress={handleKeywordInputKeyPress}
                                    placeholder="Enter a keyword..."
                                />
                                <button
                                    className="keyword-add-btn"
                                    onClick={handleAddKeyword}
                                    disabled={!newKeywordInput.trim() || editingKeywords.includes(newKeywordInput.trim())}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                        <div className="keyword-edit-footer">
                            <button className="keyword-edit-cancel" onClick={handleCancelKeywordEdit}>
                                Cancel
                            </button>
                            <button className="keyword-edit-save" onClick={handleSaveKeywords}>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {favoriteRenameModal.visible && (
                <FavoriteRenameModal
                    visible={favoriteRenameModal.visible}
                    folderId={favoriteRenameModal.folderId}
                    currentName={favoriteRenameModal.currentName}
                    onClose={() => setFavoriteRenameModal({ visible: false, folderId: null, currentName: "" })}
                    onUpdate={(newName: string) => {
                        setFolders(prev => prev.map(folder =>
                            folder.id === favoriteRenameModal.folderId
                                ? { ...folder, name: newName }
                                : folder
                        ));
                        setFavorites(prev => prev.map(folder =>
                            folder.id === favoriteRenameModal.folderId
                                ? { ...folder, name: newName }
                                : folder
                        ));
                    }}
                />
            )}

            <button className="hidden-toggle-corner" onClick={toggleHiddenVisibility} title={showHidden ? "Hidden Folders are Visible" : "Hidden Folders are Hidden"} aria-label={showHidden ? "Hidden Folders are Visible" : "Hidden Folders are Hidden"}>
                {!showHidden ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                        <line x1="2" y1="2" x2="22" y2="22" />
                    </svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                    </svg>
                )}
            </button>
        </div>
    );
};

export default FolderList;
